import { useEffect, useState } from 'react';
import { getBaseballCardByLogin } from 'src/api/baseball-card-service';
import { useIsMounted } from 'src/hooks/useIsMounted';
import { logger } from 'src/logger';
import { buildWorkDocsFolderUrl } from '../utils/workDocsUrl';

interface UsePersistedWorkDocsFolderUrlReturn {
  /** Web URL for the persisted WorkDocs root folder, or null if none is saved / fetch failed. */
  url: string | null;
  /** True while the baseball-card fetch is in flight. */
  isLoading: boolean;
}

/**
 * Resolves the "Open My WorkDocs Folder" link WITHOUT a live WorkDocs token.
 *
 * The provisioning hook only surfaces the folder link when a token is present (it lists the
 * WorkDocs home to confirm THIS quarter's folder). For a returning user whose token has expired,
 * this hook provides a token-free fallback: it reads the `workdocsFolderId` persisted on the
 * user's baseball card (`getBaseballCardByLogin` — a SigV4 REST call, no WorkDocs token) and
 * builds a folder URL from it. Note the persisted id may point to a PRIOR quarter's folder (the
 * folder name is quarter-scoped); surfacing it regardless is the intended behavior here.
 *
 * Best-effort: any failure (or no saved id) resolves to `url: null`, letting the container fall
 * back to its muted "set up in the wizard" state.
 */
export const usePersistedWorkDocsFolderUrl = (alias: string): UsePersistedWorkDocsFolderUrlReturn => {
  const [url, setUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const isMounted = useIsMounted();

  useEffect(() => {
    if (!alias) {
      setUrl(null);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    (async () => {
      try {
        const response = await getBaseballCardByLogin(alias);
        const folderId = response?.baseballCard.workdocsFolderId;
        if (cancelled || !isMounted.current) return;
        setUrl(folderId ? buildWorkDocsFolderUrl(folderId) : null);
      } catch (err) {
        logger.warn('Baseball Cards: could not resolve persisted WorkDocs folder URL', { alias, error: err });
        if (!cancelled && isMounted.current) setUrl(null);
      } finally {
        if (!cancelled && isMounted.current) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [alias, isMounted]);

  return { url, isLoading };
};
